import compactObject from '../../util/compactObject';
import getFields from '../elements/getFields';
import getFieldValue from '../elements/getFieldValue';

const KNOWN_FIELDS = [
  'abtest', 'callback_time', 'correlation_id', 'country_code', 'dialer_consent_accepted',
  'email', 'full_name', 'leadform', 'password', 'phone_number', 'referred_by_url', 'spells', 'subjects',
  'tutoring-type', 'vtcampaign', 'vtmedium', 'vtplacement', 'vtsource', 'zip_code'
];

const KNOWN_TUTOR_FIELDS = [
  'callback_time', 'correlation_id', 'country_code', 'dialer_consent_accepted', 'email', 'first_name',
  'last_name', 'password', 'phone_number', 'referred_by_url', 'spells', 'subjects', 'tutoring-type',
  'vtcampaign', 'vtmedium', 'vtplacement', 'vtsource', 'zip_code'
];

/**
 * Wizard leadform answers used by Sales and BI. By default, will not include
 * known fields like name, email, etc, or hidden fields like referrer, ab_test, etc.
 *
 * @param {Element} form Form currently being validated
 * @param {Array} excludedFields Fields to not include in the output, eg. hidden fields
 *   and known (contact-related) inputs/textareas
 * @returns {Object} Object of Wizard panel ids (keys) and user's answers (values)
 */
export function getCustomFormFields(form, excludedFields = KNOWN_FIELDS) {
  return compactObject(
    getFields(form)
      .filter(field => !excludedFields.includes(field.name))

      // if we have queried checkboxes, only get checked ones
      .filter(field => ['checkbox', 'radio'].includes(field.type) ? field.checked : true)

      // map into a {name: value} object
      .map(field => field.name)
      .reduce((obj, fieldName) => ({ ...obj, [fieldName]: getFieldValue(form, fieldName) }), {})
  );
}

/**
 * Returns a string of all additional form fields concatenated for the `message` field.
 *
 * @param {Element} form - The form to query field from
 * @param {Object} formFields - The form fields to convert into a string
 * @returns {String} Wizard answers like "panel-name: answer | panel-name: answer | ..."
 */
export default function getMessageValue(form, formFields = getCustomFormFields(form)) {
  return Object.keys(formFields)
    .map(key => `${key}: ${formFields[key]}`)
    .join(' | ');
}

/**
 * Returns a string of all additional form fields concatenated for the `tutor_message_note` field.
 * Similar to the `message` field, but includes PID fields.
 *
 * @param {Element} form - The form to query field from
 * @returns {String} Wizard answers like "panel-name: answer | panel-name: answer | ..."
 */
export function getTutorSummaryNote(form) {
  const formFields = getCustomFormFields(form, KNOWN_TUTOR_FIELDS);
  return getMessageValue(form, formFields);
}

/**
 * BI requires the answers as a hash at root level as there is currently no support
 * for nested hashes in the 'anything goes' params
 *
 * @param {Element} form - The form to query field from
 * @param {Object} formFields - The form fields to convert into a string
 * @returns {Object} Wizard answers like { "lead_form.panel_name": "answer", ... }
 */
export function getAdditionalFormFields(form, formFields = getCustomFormFields(form)) {
  return Object.fromEntries(
    Object.entries(formFields).map(
      ([key, value]) => [`lead_form.${key.replace('-', '_')}`, value]
    )
  );
}
